import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";
import { Col, Row } from "reactstrap";
import { CreateEquipmentValidationFields, EquipmentUtilities } from "../../../EquipmentUtilities";
import useValidationSchema from "../../../components/Validations";
import { useAvailableModulesContext } from "../../../context/AvailableModulesContext";
import Routes from "../../../helpers/Routes";
import Toast from "../../../helpers/Toast";
import { SearchByClientName } from "../../../models/Client/SearchByClientName";
import { CreateEquipmentDTO } from "../../../models/Equipment/CreateEquipmentDTO";
import { ClientService } from "../../../services/ClientService";
import { EquipmentService } from "../../../services/EquipmentService";
import ButtonComponent from "../../ButtonComponent";
import InputComponent from "../../InputComponent";
import SearchSelectComponent from "../../SearchSelectComponent";
import { SelectItemsProps } from "../../Select2Component";
import SearchOrTypeSelectComponent from "../../SearchOrTypeSelectComponent";
import CheckboxComponent from "../../CheckboxComponent";
import { Utilities } from "../../../helpers/Utilities";

export default function EquipmentCreateA() {
    const intl = useIntl();
    const navigate = useNavigate();
    const clientService = new ClientService();
    const equipmentService = new EquipmentService();

    const { availableModules } = useAvailableModulesContext();
    const [isActiveCarPlate, setIsActiveCarPlate] = useState<boolean>(false);
    const [isActiveSoftwareModule, setIsActiveSoftwareModule] = useState<boolean>(false);
    const [isActiveWarrantySupplier, setIsActiveWarrantySupplier] = useState<boolean>(false);
    const [isActiveWarrantySeller, setIsActiveWarrantySeller] = useState<boolean>(false);
    const [isActiveRecallModule, setIsActiveRecallModule] = useState<boolean>(false);

    useEffect(() => {
        if (availableModules.includes("IsActiveCarPlate")) {
            setIsActiveCarPlate(true);
        }
        if (availableModules.includes("IsActiveSoftwareModule")) {
            setIsActiveSoftwareModule(true);
        }
        if (availableModules.includes("IsActiveWarrantySeller")) {
            setIsActiveWarrantySeller(true);
        }
        if (availableModules.includes("IsActiveWarrantySupplier")) {
            setIsActiveWarrantySupplier(true);
        }
        if (availableModules.includes("IsActiveRecallModule")) {
            setIsActiveRecallModule(true);
        }
    }, [availableModules]);

    //Loadings
    const [isButtonCreateLoading, setIsButtonCreateLoading] = useState<boolean>(false);

    const [clientSelected, setClientSelected] = useState<SelectItemsProps | null>(null);
    const [brandSelected, setBrandSelected] = useState<SelectItemsProps | null>(null);
    const [modelSelected, setModelSelected] = useState<SelectItemsProps | null>(null);

    const [equipment, setEquipment] = useState<CreateEquipmentDTO>({} as CreateEquipmentDTO);

    // Validações
    const fields = CreateEquipmentValidationFields(isActiveCarPlate);
    const schemaClientValidation = useValidationSchema(fields);

    const createEquipment = async () => {
        setIsButtonCreateLoading(true);

        const createEquipment: CreateEquipmentDTO = {
            erpId: null,
            name: equipment?.name ?? null,

            carPlate: equipment?.carPlate ?? null,
            carPlateValidation: isActiveCarPlate ?? null,

            serialNumber: equipment?.serialNumber ?? null,
            softwareName: equipment.softwareName ?? null,
            softwareVersion: equipment.softwareVersion ?? null,
            brand: brandSelected?.label ?? null,
            model: modelSelected?.label ?? null,

            clientErpId: clientSelected?.value ?? null,

            warrantySupplierDescription: equipment?.warrantySupplierDescription ?? null,
            warrantySupplierStartDate: equipment?.warrantySupplierStartDate ?? null,
            warrantySupplierEndDate: equipment?.warrantySupplierEndDate ?? null,
            warrantySupplierIncludesParts: equipment?.warrantySupplierIncludesParts ?? null,
            warrantySupplierIncludesTravel: equipment?.warrantySupplierIncludesTravel ?? null,
            warrantySupplierIncludesWork: equipment?.warrantySupplierIncludesWork ?? null,

            warrantySellerDescription: equipment?.warrantySellerDescription ?? null,
            warrantySellerStartDate: equipment?.warrantySellerStartDate ?? null,
            warrantySellerEndDate: equipment?.warrantySellerEndDate ?? null,
            warrantySellerIncludesParts: equipment?.warrantySellerIncludesParts ?? null,
            warrantySellerIncludesTravel: equipment?.warrantySellerIncludesTravel ?? null,
            warrantySellerIncludesWork: equipment?.warrantySellerIncludesWork ?? null,

            recallDescription: equipment?.recallDescription ?? null,
            recallStartDate: equipment?.recallStartDate ?? null,
            recallEndDate: equipment?.recallEndDate ?? null,
            recallIncludesSecurity: equipment?.recallIncludesSecurity ?? null,
            recallIncludesRisk: equipment?.recallIncludesRisk ?? null,
            recallIncludesDefect: equipment?.recallIncludesDefect ?? null,
        };

        const { error } = schemaClientValidation.validate(createEquipment);
        if (error) {
            Toast.Show("error", error.details.map(detail => detail.message).join(', '));
            setIsButtonCreateLoading(false);
            return;
        }

        const response = await equipmentService.Create(createEquipment);

        if (response.success !== true) {
            Toast.Show("error", response.message);
            setIsButtonCreateLoading(false);
            return;
        }

        Toast.Show("success", response.message);
        setIsButtonCreateLoading(false);
        navigate(`${Routes.equipments.details}/${response.obj}`);
    };

    const GetByClient = async (value: string): Promise<SelectItemsProps[]> => {
        var data: SearchByClientName = {
            name: value,
        };
        var response = await clientService.SearchByName(data);
        if (response.success === false || response.obj === null) {
            Toast.Show("error", response.message);
            return [];
        }

        const list: SelectItemsProps[] = [];

        response.obj.forEach((element: any) => {
            var item: SelectItemsProps = {
                value: element.erpId,
                label: element.name,
            };
            list.push(item);
        });

        return list;
    };

    //#region codigo relevante para criar novo equipamento através de navegação do cliente
    const location = useLocation();
    useEffect(() => {
        if (location.state) {
            const { clientErpId, clientName } = location.state;
            if (clientErpId && clientName) {
                setClientSelected({ value: clientErpId, label: clientName });
            }
        }
    }, [location.state]);
    //#endregion

    return (
        <>
            <div>
                <Col xl={6}>
                    <label style={{ margin: "0px", padding: "13px" }}>
                        <b>
                            <FormattedMessage id="Screens.EquipmentCreate.SubTitle2" />
                        </b>
                    </label>
                    <Row
                        style={{
                            margin: "0px",
                            padding: "0px",
                            alignItems: "flex-end",
                            alignContent: "flex-end",
                        }}
                    >
                        <SearchSelectComponent
                            xl={9}
                            lg={6}
                            md={9}
                            sm={9}
                            xs={12}
                            label={intl.formatMessage({
                                id: "Screens.Equipments.Info.Client",
                            })}
                            loadOptions={GetByClient}
                            onChange={(e) => {
                                setClientSelected(e);
                            }}
                            value={clientSelected}
                            style={{ width: "100%" }}
                        />
                    </Row>
                    <label style={{ margin: "0px", padding: "13px" }}>
                        <b>
                            <FormattedMessage id="Screens.EquipmentCreate.SubTitle" />
                        </b>
                    </label>

                    <Row
                        style={{
                            margin: "0px",
                            padding: "0px",
                            alignItems: "flex-end",
                            alignContent: "flex-end",
                        }}
                    >
                        <InputComponent
                            xl={9}
                            lg={6}
                            md={9}
                            sm={9}
                            xs={12}
                            style={{ width: "100%" }}
                            type={"text"}
                            value={equipment?.name}
                            label={intl.formatMessage({
                                id: "Screens.Equipments.Info.Name",
                            })}
                            readOnly={isButtonCreateLoading}
                            onChange={(name: string) => {
                                setEquipment({ ...equipment!, name: name });
                            }}
                        />
                    </Row>

                    {isActiveCarPlate && (
                        <Row
                            style={{
                                margin: "0px",
                                padding: "0px",
                                alignItems: "flex-end",
                                alignContent: "flex-end",
                            }}
                        >
                            <InputComponent
                                xl={3}
                                lg={2}
                                md={3}
                                sm={3}
                                xs={12}
                                style={{ width: "100%" }}
                                type={"text"}
                                value={equipment?.carPlate}
                                label={intl.formatMessage({
                                    id: "Screens.Equipments.Info.CarPlate",
                                })}
                                readOnly={isButtonCreateLoading}
                                onChange={(carPlate: string) => {
                                    setEquipment({ ...equipment!, carPlate: carPlate });
                                }}
                            />
                        </Row>
                    )}

                    <Row
                        style={{
                            margin: "0px",
                            padding: "0px",
                            alignItems: "flex-end",
                            alignContent: "flex-end",
                        }}
                    >
                        <InputComponent
                            xl={3}
                            lg={2}
                            md={3}
                            sm={3}
                            xs={12}
                            style={{ width: "100%" }}
                            type={"text"}
                            value={equipment?.serialNumber}
                            label={intl.formatMessage({
                                id: "Screens.Equipments.Info.SerialNumber",
                            })}
                            readOnly={isButtonCreateLoading}
                            onChange={(serialNumber: string) => {
                                setEquipment({ ...equipment!, serialNumber: serialNumber });
                            }}
                        />

                        <SearchOrTypeSelectComponent
                            xl={3}
                            label={intl.formatMessage({ id: "Screens.Equipments.Info.Brand" })}
                            loadOptions={EquipmentUtilities.SearchBrand}
                            onChange={(e: SelectItemsProps | null) => setBrandSelected(e)}
                            value={brandSelected}
                        />

                        <SearchOrTypeSelectComponent
                            xl={3}
                            label={intl.formatMessage({ id: "Screens.Equipments.Info.Model" })}
                            loadOptions={EquipmentUtilities.SearchModel}
                            onChange={(e: SelectItemsProps | null) => setModelSelected(e)}
                            value={modelSelected}
                            isDisabled={!brandSelected}
                        />
                    </Row>

                    {isActiveSoftwareModule &&
                        <Row
                            style={{
                                margin: "0px",
                                padding: "0px",
                                alignItems: "flex-end",
                                alignContent: "flex-end",
                            }}
                        >
                            <InputComponent
                                xl={3}
                                lg={2}
                                md={3}
                                sm={3}
                                xs={12}
                                style={{ width: "100%" }}
                                type={"text"}
                                value={equipment?.softwareName}
                                label={intl.formatMessage({
                                    id: "Screens.Equipments.Info.SoftwareName",
                                })}
                                readOnly={isButtonCreateLoading}
                                onChange={(softwareName: string) => {
                                    setEquipment({ ...equipment!, softwareName: softwareName });
                                }}
                            />

                            <InputComponent
                                xl={3}
                                lg={2}
                                md={3}
                                sm={3}
                                xs={12}
                                style={{ width: "100%" }}
                                type={"text"}
                                value={equipment?.softwareVersion}
                                label={intl.formatMessage({
                                    id: "Screens.Equipments.Info.SoftwareVersion",
                                })}
                                readOnly={isButtonCreateLoading}
                                onChange={(softwareVersion: string) => {
                                    setEquipment({ ...equipment!, softwareVersion: softwareVersion });
                                }}
                            />
                        </Row>
                    }

                    {isActiveWarrantySupplier && (
                        <>
                            <label style={{ margin: "0px", padding: "13px" }}>
                                <b>
                                    <FormattedMessage id="Screens.EquipmentCreate.WarrantySupplierSection" />
                                </b>
                            </label>

                            <Row
                                style={{
                                    margin: "0px",
                                    padding: "0px",
                                    alignItems: "flex-end",
                                    alignContent: "flex-end",
                                }}
                            >
                                {/* Fornecedor */}
                                <InputComponent
                                    xl={3}
                                    lg={3}
                                    md={3}
                                    sm={3}
                                    xs={12}
                                    style={{ width: "100%" }}
                                    type="date"
                                    value={equipment.warrantySupplierStartDate}
                                    label={intl.formatMessage({ id: "Screens.Equipment.WarrantySupplierStartDate" })}
                                    readOnly={isButtonCreateLoading}
                                    onChange={(warrantySupplierStartDate: Date) => {
                                        setEquipment({ ...equipment!, warrantySupplierStartDate: warrantySupplierStartDate });
                                    }}
                                />

                                <InputComponent
                                    xl={3}
                                    lg={3}
                                    md={3}
                                    sm={3}
                                    xs={12}
                                    style={{ width: "100%" }}
                                    type="date"
                                    value={equipment.warrantySupplierEndDate}
                                    label={intl.formatMessage({ id: "Screens.Equipment.WarrantySupplierEndDate" })}
                                    readOnly={isButtonCreateLoading}
                                    onChange={(warrantySupplierEndDate: Date) => {
                                        setEquipment({ ...equipment!, warrantySupplierEndDate: warrantySupplierEndDate });
                                    }}
                                />
                            </Row>

                            <Row
                                style={{
                                    margin: "0px",
                                    marginTop: "10px",
                                    padding: "0px",
                                    alignItems: "flex-end",
                                    alignContent: "flex-end",
                                }}
                            >
                                <Col xl={3} lg={3} md={12} sm={12} xs={12}>
                                    <CheckboxComponent
                                        checked={equipment.warrantySupplierIncludesParts}
                                        disabled={isButtonCreateLoading}
                                        onChange={() => {
                                            setEquipment({ ...equipment!, warrantySupplierIncludesParts: !equipment?.warrantySupplierIncludesParts });
                                        }}
                                        label={intl.formatMessage({ id: "Screens.Equipment.WarrantySupplierIncludesParts" })}
                                    />
                                </Col>

                                <Col xl={3} lg={3} md={12} sm={12} xs={12}>
                                    <CheckboxComponent
                                        checked={equipment.warrantySupplierIncludesTravel}
                                        disabled={isButtonCreateLoading}
                                        onChange={() => {
                                            setEquipment({ ...equipment!, warrantySupplierIncludesTravel: !equipment?.warrantySupplierIncludesTravel });
                                        }}
                                        label={intl.formatMessage({ id: "Screens.Equipment.WarrantySupplierIncludesTravel" })}
                                    />
                                </Col>

                                <Col xl={3} lg={3} md={12} sm={12} xs={12}>
                                    <CheckboxComponent
                                        checked={equipment.warrantySupplierIncludesWork}
                                        disabled={isButtonCreateLoading}
                                        onChange={() => {
                                            setEquipment({ ...equipment!, warrantySupplierIncludesWork: !equipment?.warrantySupplierIncludesWork });
                                        }}
                                        label={intl.formatMessage({ id: "Screens.Equipment.WarrantySupplierIncludesWork" })}
                                    />
                                </Col>
                            </Row>

                            <Row
                                style={{
                                    margin: "0px",
                                    padding: "0px",
                                    alignItems: "flex-end",
                                    alignContent: "flex-end",
                                }}
                            >
                                <InputComponent
                                    xl={9}
                                    lg={9}
                                    md={9}
                                    sm={9}
                                    xs={12}
                                    style={{ width: "100%" }}
                                    type="textArea"
                                    value={equipment?.warrantySupplierDescription}
                                    label={intl.formatMessage({ id: "Screens.Equipment.WarrantySupplierDescription" })}
                                    readOnly={isButtonCreateLoading}
                                    onChange={(warrantySupplierDescription: string) => {
                                        setEquipment({ ...equipment!, warrantySupplierDescription });
                                    }}
                                />
                            </Row>
                        </>
                    )}

                    {isActiveWarrantySeller && (
                        <>
                            <label style={{ margin: "0px", padding: "13px" }}>
                                <b>
                                    <FormattedMessage id="Screens.EquipmentCreate.WarrantySellerSection" />
                                </b>
                            </label>

                            <Row
                                style={{
                                    margin: "0px",
                                    padding: "0px",
                                    alignItems: "flex-end",
                                    alignContent: "flex-end",
                                }}
                            >
                                <InputComponent
                                    xl={3}
                                    lg={3}
                                    md={3}
                                    sm={3}
                                    xs={12}
                                    style={{ width: "100%" }}
                                    type="date"
                                    value={equipment.warrantySellerStartDate}
                                    label={intl.formatMessage({ id: "Screens.Equipment.WarrantySellerStartDate" })}
                                    readOnly={isButtonCreateLoading}
                                    onChange={(warrantySellerStartDate: Date) => {
                                        setEquipment({ ...equipment!, warrantySellerStartDate: warrantySellerStartDate });
                                    }}
                                />

                                <InputComponent
                                    xl={3}
                                    lg={3}
                                    md={3}
                                    sm={3}
                                    xs={12}
                                    style={{ width: "100%" }}
                                    type="date"
                                    value={equipment.warrantySellerEndDate}
                                    label={intl.formatMessage({ id: "Screens.Equipment.WarrantySellerEndDate" })}
                                    readOnly={isButtonCreateLoading}
                                    onChange={(warrantySellerEndDate: Date) => {
                                        setEquipment({ ...equipment!, warrantySellerEndDate: warrantySellerEndDate });
                                    }}
                                />
                            </Row>

                            <Row
                                style={{
                                    margin: "0px",
                                    marginTop: "10px",
                                    padding: "0px",
                                    alignItems: "flex-end",
                                    alignContent: "flex-end",
                                }}
                            >
                                <Col xl={3} lg={3} md={12} sm={12} xs={12}>
                                    <CheckboxComponent
                                        checked={equipment.warrantySellerIncludesParts}
                                        disabled={isButtonCreateLoading}
                                        onChange={() => {
                                            setEquipment({ ...equipment!, warrantySellerIncludesParts: !equipment?.warrantySellerIncludesParts });
                                        }}
                                        label={intl.formatMessage({ id: "Screens.Equipment.WarrantySellerIncludesParts" })}
                                    />
                                </Col>

                                <Col xl={3} lg={3} md={12} sm={12} xs={12}>
                                    <CheckboxComponent
                                        checked={equipment.warrantySellerIncludesTravel}
                                        disabled={isButtonCreateLoading}
                                        onChange={() => {
                                            setEquipment({ ...equipment!, warrantySellerIncludesTravel: !equipment?.warrantySellerIncludesTravel });
                                        }}
                                        label={intl.formatMessage({ id: "Screens.Equipment.WarrantySellerIncludesTravel" })}
                                    />
                                </Col>

                                <Col xl={3} lg={3} md={12} sm={12} xs={12}>
                                    <CheckboxComponent
                                        checked={equipment.warrantySellerIncludesWork}
                                        disabled={isButtonCreateLoading}
                                        onChange={() => {
                                            setEquipment({ ...equipment!, warrantySellerIncludesWork: !equipment?.warrantySellerIncludesWork });
                                        }}
                                        label={intl.formatMessage({ id: "Screens.Equipment.WarrantySellerIncludesWork" })}
                                    />
                                </Col>
                            </Row>

                            <Row
                                style={{
                                    margin: "0px",
                                    padding: "0px",
                                    alignItems: "flex-end",
                                    alignContent: "flex-end",
                                }}
                            >

                                <InputComponent
                                    xl={9}
                                    lg={9}
                                    md={9}
                                    sm={9}
                                    xs={12}
                                    style={{ width: "100%" }}
                                    type="textArea"
                                    value={equipment?.warrantySellerDescription}
                                    label={intl.formatMessage({ id: "Screens.Equipment.WarrantySellerDescription" })}
                                    readOnly={isButtonCreateLoading}
                                    onChange={(warrantySellerDescription: string) => {
                                        setEquipment({ ...equipment!, warrantySellerDescription });
                                    }}
                                />
                            </Row>
                        </>
                    )}

                    {isActiveRecallModule && (
                        <>
                            <label style={{ margin: "0px", padding: "13px" }}>
                                <b>
                                    <FormattedMessage id="Screens.EquipmentCreate.RecallSection" />
                                </b>
                            </label>

                            <Row
                                style={{
                                    margin: "0px",
                                    padding: "0px",
                                    alignItems: "flex-end",
                                    alignContent: "flex-end",
                                }}
                            >
                                <InputComponent
                                    xl={3}
                                    lg={3}
                                    md={3}
                                    sm={3}
                                    xs={12}
                                    style={{ width: "100%" }}
                                    type="date"
                                    value={equipment?.recallStartDate}
                                    label={intl.formatMessage({ id: "Screens.Equipment.RecallStartDate" })}
                                    readOnly={isButtonCreateLoading}
                                    onChange={(recallStartDate: Date) => {
                                        setEquipment({ ...equipment!, recallStartDate: recallStartDate });
                                    }}
                                />

                                <InputComponent
                                    xl={3}
                                    lg={3}
                                    md={3}
                                    sm={3}
                                    xs={12}
                                    style={{ width: "100%" }}
                                    type="date"
                                    value={equipment?.recallEndDate}
                                    label={intl.formatMessage({ id: "Screens.Equipment.RecallEndDate" })}
                                    readOnly={isButtonCreateLoading}
                                    onChange={(recallEndDate: Date) => {
                                        setEquipment({ ...equipment!, recallEndDate: recallEndDate });
                                    }}
                                />
                            </Row>

                            <Row
                                style={{
                                    margin: "0px",
                                    marginTop: "10px",
                                    padding: "0px",
                                    alignItems: "flex-end",
                                    alignContent: "flex-end",
                                }}
                            >
                                <Col xl={3} lg={3} md={12} sm={12} xs={12}>
                                    <CheckboxComponent
                                        checked={equipment?.recallIncludesSecurity}
                                        disabled={isButtonCreateLoading}
                                        onChange={() => {
                                            setEquipment({ ...equipment!, recallIncludesSecurity: !equipment?.recallIncludesSecurity });
                                        }}
                                        label={intl.formatMessage({ id: "Screens.Equipment.RecallIncludesSecurity" })}
                                    />
                                </Col>

                                <Col xl={3} lg={3} md={12} sm={12} xs={12}>
                                    <CheckboxComponent
                                        checked={equipment?.recallIncludesRisk}
                                        disabled={isButtonCreateLoading}
                                        onChange={() => {
                                            setEquipment({ ...equipment!, recallIncludesRisk: !equipment?.recallIncludesRisk });
                                        }}
                                        label={intl.formatMessage({ id: "Screens.Equipment.RecallIncludesRisk" })}
                                    />
                                </Col>

                                <Col xl={3} lg={3} md={12} sm={12} xs={12}>
                                    <CheckboxComponent
                                        checked={equipment?.recallIncludesDefect}
                                        disabled={isButtonCreateLoading}
                                        onChange={() => {
                                            setEquipment({ ...equipment!, recallIncludesDefect: !equipment?.recallIncludesDefect });
                                        }}
                                        label={intl.formatMessage({ id: "Screens.Equipment.RecallIncludesDefect" })}
                                    />
                                </Col>
                            </Row>

                            <Row
                                style={{
                                    margin: "0px",
                                    padding: "0px",
                                    alignItems: "flex-end",
                                    alignContent: "flex-end",
                                }}
                            >

                                <InputComponent
                                    xl={9}
                                    lg={9}
                                    md={9}
                                    sm={9}
                                    xs={12}
                                    style={{ width: "100%" }}
                                    type="textArea"
                                    value={equipment?.recallDescription}
                                    label={intl.formatMessage({ id: "Screens.Equipment.RecallDescription" })}
                                    readOnly={isButtonCreateLoading}
                                    onChange={(recallDescription: string) => {
                                        setEquipment({ ...equipment!, recallDescription });
                                    }}
                                />
                            </Row>
                        </>
                    )}
                </Col>

                <Row style={{ margin: "0px", padding: "0px" }}>
                    <Col xl={1} lg={1} md={1} sm={1} xs={1}>
                        <ButtonComponent
                            loading={isButtonCreateLoading}
                            style={{ width: "100%", marginTop: "10px" }}
                            text={intl.formatMessage({ id: "EquipmentCreateA.Save" })}
                            onClick={() => createEquipment()}
                        />
                    </Col>
                </Row>
            </div >
        </>
    );
}
